import { observer } from "mobx-react-lite";
import MainLayout from "../MainLayout";
import { useNavigate } from "react-router-dom";
import { VerticalLayout } from "../../common/common-layout";
import { TitleLargeMedium } from "../../common/common-text";
import BTBox from "../../common/components/BTBox";
import { useEffect, useState } from "react";
import Patent from "../../model/board/Patent";
import { fetchPatentList, fetchPatentType, requestDeleteAllFiles, requestDeletePatentDocuments } from "../../service/BoardService";
import { dialogStore } from "../../store/DialogStore";
import BTImageList from "../../common/components/board/BTImageList";
import { formatDateToYMD } from "../../common/utils/DateUtils";

const PatentBoard = () => {
  return (
    <MainLayout>
      <PatentBoardBody />
    </MainLayout>
  );
};

export default PatentBoard;

const PatentBoardBody = observer(() => {
  const navigate = useNavigate();
  const [patents, setPatents] = useState<Patent[]>([]);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [patentTypeOptions, setPatentTypeOptions] = useState<{ label: string; value: string }[]>([]);
  const [selectedPatentType, setSelectedPatentType] = useState<string>('all');

  const [sortOptions] = useState<{ label: string; value: 'title' | 'displayDate' | 'createdAt' }[]>([
      { label: '제목', value: 'title' },
      { label: '취득일자', value: 'displayDate' }, // 마케팅의 displayDate 역할
      { label: '생성시각', value: 'createdAt' },
  ]);
  const [selectedSort, setSelectedSort] = useState<'title' | 'displayDate' | 'createdAt'>('createdAt');

  const loadData = async () => {
    const result = await fetchPatentList();
    setPatents(result);

    const types = await fetchPatentType();
    setPatentTypeOptions(types.map(tab => ({
      label: tab.tabName,
      value: tab.tabCode
    })));
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleWriteClick = () => {
    navigate("/board/patent/add", {
      state: patentTypeOptions
    });
  };

  const handleDeleteClick = async (selectedIndexes: number[]) => {
    if (selectedIndexes.length === 0) {
      dialogStore.showDialog({
        title: '알림',
        message: '삭제할 아이템을 선택해주세요.',
        onConfirm: () => {},
      });
      return;
    }

    const deleteConfirmed = await dialogStore.showAsyncDialog({
      title: '경고',
      message: `선택된 ${selectedIndexes.length}개의 특허건을 정말로 삭제하시겠습니까?`,
      isCaution: true,
    });

    if (!deleteConfirmed) {
      return;
    }

    try {
      const selectedPatentIds: string[] = selectedIndexes
        .map((idx) => patents[idx]?.id)
        .filter((id): id is string => !!id); // null/undefined 제거

      await requestDeletePatentDocuments(selectedPatentIds);

      await Promise.all(
        selectedPatentIds.map((docId) =>
          requestDeleteAllFiles("patent", docId)
        )
      );

      dialogStore.showDialog({
        title: '삭제 완료',
        message: `${selectedPatentIds.length}개의 특허건이 삭제되었습니다.`,
        onConfirm: () => {
          setSelectedRows([]);
          loadData();
        },
        onClose: () => {
          setSelectedRows([]);
          loadData();
        },
      });

    } catch (e) {
      console.error('특허 삭제 실패', e);
      dialogStore.showDialog({
        title: '오류',
        message: `특허 삭제 중 오류가 발생했습니다.`,
        onConfirm: () => {},
        onClose: () => {},
      });
    }
  };

  const handleCardClick = (index: number): void => {
    navigate('/board/patent/detail', {
      state: {
        patent: patents[index],
        patentTypeOptions: patentTypeOptions
      }
    });
  };

  return (
    <VerticalLayout height={'auto'} paddingLeft={36} paddingRight={36}>
      <TitleLargeMedium>특허 관리 게시판</TitleLargeMedium>
      <BTBox height="40px" />
      <BTImageList
        data={patents.map(d => ({
          id: d.id,
          title: d.title,
          imageSrc: d.representImage,
          type: d.patentType, // 🔥 필터 기준 필드 포함
          displayDate: d.acquisitionDate ? formatDateToYMD(d.acquisitionDate) : '-',
          isPublished: d.isPublished,
          createdAt: d.createdAt,
        }))}
        onAddClick={handleWriteClick}
        handleCardClick={handleCardClick}
        onDeleteClick={handleDeleteClick}
        addButtonText="추가"
        deleteButtonText="삭제"
        checkboxSelection={true}
        filterTitle="특허 유형"
        filterOptions={[{ label: '전체', value: 'all' }, ...patentTypeOptions]}
        filterValue={selectedPatentType}
        onFilterChange={setSelectedPatentType}
        sortTitle="정렬"
        sortOptions={sortOptions}
        sortValue={selectedSort}
        onSortChange={setSelectedSort}
        pageStateKey="board.patent.list"
      />
    </VerticalLayout>
  );
});